import React from 'react';
import { PaymentMethods } from './DepositService';
import { useController, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation } from 'react-router-dom';

import { getDepositPromotions, getDepositRequestTypes } from '../../../api/users';
import useGetLimit from './useGetLimit';

type Inputs = {
	amount: number;
};

const useOnboardingDepositScreenState = () => {
	const { limitItem } = useGetLimit();
	const location = useLocation();

	const currentClientId = React.useMemo(() => {
		if (location.pathname.startsWith('/betting')) return 1;

		if (location.pathname.startsWith('/fantasy')) return 2;
	}, [location]);

	const schema = React.useMemo(() => {
		return yup
			.object()
			.shape({
				amount: yup
					.number()
					.test('max', 'You have reached your deposit limit', (value) => {
						if (!limitItem?.limit) return true;
						if (!value || !limitItem) return false;
						return value <= (limitItem?.limit - limitItem?.used) / 100;
					})
					.typeError('Amount must be a number')
					.required('Amount is required')
					.min(1, 'Amount must be greater than 0'),
			})

			.required();
	}, [limitItem]);

	const { control, handleSubmit } = useForm<Inputs>({
		resolver: yupResolver(schema),
		mode: 'all',
	});

	const {
		field: { value: amount, onChange: onAmountChange },
		fieldState: { error },
	} = useController({
		control,
		name: 'amount',
		defaultValue: 0,
	});

	const [selectedPaymentId, setSelectedPaymentId] = React.useState<number>();
	const [depositPromotions, setDepositPromotions] = React.useState<DepositPromotion[]>([]);
	const [selectedPromotion, setSelectedPromotion] = React.useState<DepositPromotion>();
	const onSelectPromotion = React.useCallback((promotion: DepositPromotion) => {
		setSelectedPromotion(promotion);
	}, []);

	const [availablePayment, setAvailablePayment] = React.useState({
		card: {
			visible: true,
		},
		apple_pay: {
			visible: false,
			merchantId: '',
		},
	});

	React.useEffect(() => {
		(async () => {
			try {
				setSelectedPaymentId(PaymentMethods.Card);
				const { data } = await getDepositPromotions();

				data.sort((a) => (a.client.id === currentClientId ? -1 : 1));

				setDepositPromotions(data);
				setSelectedPromotion(data[0]);
			} catch (error) {
				console.log(error);
			}
		})();
	}, [currentClientId]);

	const onSelectPayment = React.useCallback((id) => {
		setSelectedPaymentId(id);
	}, []);

	return {
		selectedPromotion,
		onSelectPromotion,
		depositPromotions,
		amount,
		onAmountChange,
		availablePayment,
		selectedPaymentId,
		onSelectPayment,
		error,
		handleSubmit,
	};
};
export { useOnboardingDepositScreenState };
